import React, { useState, useEffect, useMemo, useCallback, useRef, useContext } from "react";
import { Outlet, useNavigate, useSearchParams } from "react-router-dom";
import backend from "../../backend";
import AuthContext from "../../AuthContext";
import useTitle from "../../hooks/useTitle";
import usePulidoLink from "../../hooks/usePulidoLink";
import ExpandableTable from "../../components/design-system/ExpandableTable";
import ConfirmModal from "../../components/design-system/ConfirmModal";
import DocumentAttacher from "../../components/design-system/DocumentAttacher";
import SimbolosToolbar from "../../components/design-system/SimbolosToolbar";
import EntityLookup from "../../components/design-system/EntityLookup";
import PieceLookup from "../../components/design-system/PieceLookup";
import { maskRut } from "../../utils";

const fmt = d => d ? new Date(d).toLocaleDateString("es-CL", {
  day: "2-digit", month: "short", year: "2-digit",
}) : "—";

const fmtN = n => Number(n || 0).toLocaleString("es-CL");

// Suma de precios de componentes × cantidad. Si la pieza es conjunto,
// se compara con piece.price para detectar markup/descuento del armado.
// Devuelve 0 si no hay componentes o si vienen sin populate.
const sumComponentes = (piece) => {
  const list = piece?.components || [];
  let total = 0;
  for (const c of list) {
    const unit = Number(c.piece?.price) || 0;
    const qty  = Number(c.quantity) || 1;
    total += unit * qty;
  }
  return total;
};

// P3 = precio neto desde tiempos de procesos (SIN material, SIN utilidad).
// Procesos internos: tarifa_máquina × duración / 60.
// Procesos externos: precio fijo del proveedor.
// Devuelve 0 si no hay datos suficientes (sin procesos, sin constants, etc).
const calcP3 = (piece) => {
  if (!piece?.processes?.length) return 0;
  const prices = piece.constants?.prices;
  let total = 0;
  for (const proc of piece.processes) {
    if (proc.external) {
      total += Number(proc.price) || 0;
    } else if (proc.machineType && prices) {
      const mtId = proc.machineType._id || proc.machineType;
      const row = prices.find(r => String(r.machineType) === String(mtId));
      if (row) {
        const rate = row[proc.priceSelected || "price"] || 0;
        total += (rate * (proc.duration || 0)) / 60;
      }
    }
  }
  return Math.round(total);
};

const STATUS_LABELS = {
  pending:   "Por definir",
  approved:  "Aprobada",
  working:   "En proceso",
  ready:     "Terminado",
  delivered: "Entregado",
};

// ─────────────────────────────────────────────────────────────────────────────

const Pieces = () => {
  useTitle("Catálogo");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // 28-ago (auditoría roles) — editar el catálogo (crear/editar/eliminar
  // piezas) es de admin/operator. El seller usa el catálogo para consultar
  // precios: vista y buscador completos, sin acciones de escritura.
  const auth = useContext(AuthContext);
  const userRoles = auth?.user?.roles || [];
  const puedeEditar = ["admin", "operator"].some(r => userRoles.includes(r));

  const [pieces, setPieces]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [materials, setMaterials] = useState([]);

  // F4-QA1: si llegan con ?search=<sku>, prellenar el filtro
  const [searchTerm, setSearchTerm]         = useState(() => searchParams.get("search") || "");
  // Cards clickables: "all" | "withP1" | "withP2" | "outOfCatalog"
  const [filter, setFilter]                 = useState("all");
  const [page, setPage]                     = useState(1);
  const PER_PAGE = 50;

  const [deleteTarget, setDeleteTarget]       = useState(null);
  const [historyByPiece, setHistoryByPiece]   = useState({});
  // Modal de detalle de precio (P1/P2/P3) — abierto desde click en un card
  const [priceDetail, setPriceDetail]         = useState(null); // { piece, mode: 'P1'|'P2'|'P3' }

  // ── data ──────────────────────────────────────────────────────────────
  const load = async () => {
    setLoading(true);
    try {
      const [pRes, mRes] = await Promise.all([
        backend.get("piece"),
        backend.get("rawMaterial"),
      ]);
      setPieces(pRes.data || []);
      setMaterials(mRes.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const loadHistory = useCallback(async pieceId => {
    if (historyByPiece[pieceId] !== undefined) return;
    // mark as loading to avoid duplicate requests
    setHistoryByPiece(prev => ({ ...prev, [pieceId]: null }));
    try {
      const res = await backend.get(`piece/${pieceId}/history`);
      setHistoryByPiece(prev => ({ ...prev, [pieceId]: res.data }));
    } catch (e) {
      console.error(e);
      setHistoryByPiece(prev => ({ ...prev, [pieceId]: { metrics: { workOrdersCount: 0 }, rows: [] } }));
    }
  }, [historyByPiece]);

  // ── filtrado + paginación ────────────────────────────────────────────
  const filtered = useMemo(() => {
    let rows = pieces.filter(p => p.status !== "archived");

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      rows = rows.filter(p =>
        (p.sku      || "").toLowerCase().includes(q) ||
        (p.name     || "").toLowerCase().includes(q) ||
        (p.details  || "").toLowerCase().includes(q) ||
        (p.material || "").toLowerCase().includes(q) ||
        (p.skuAliases || []).some(a => a.toLowerCase().includes(q))
      );
    }

    if (filter === "withP1")             rows = rows.filter(p => p.price > 0);
    else if (filter === "withP2")        rows = rows.filter(p => p.priceClient > 0);
    else if (filter === "outOfCatalog")  rows = rows.filter(p => p.notInOfficialCatalog);

    return rows;
  }, [pieces, searchTerm, filter]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE) || 1;
  const pageRows   = useMemo(() => {
    const start = (page - 1) * PER_PAGE;
    return filtered.slice(start, start + PER_PAGE);
  }, [filtered, page]);

  useEffect(() => { setPage(1); }, [searchTerm, filter]);

  // D-T51b — Link directo desde /pulido (?openId=<pieceId>&editField=price o ?openBySku=<sku>)
  const pulidoLink = usePulidoLink({
    rows: pieces,
    getSku: r => r.sku,
  });

  // Reset filters + page jump si la pieza está fuera del filtro/página
  useEffect(() => {
    if (!pulidoLink.openId || !pieces.length) return;
    const target = pieces.find(p => p._id === pulidoLink.openId);
    if (!target) return;
    const inFiltered = filtered.some(p => p._id === pulidoLink.openId);
    if (!inFiltered) {
      setSearchTerm("");
      setFilter("all");
      return;
    }
    const idx = filtered.findIndex(p => p._id === pulidoLink.openId);
    const targetPage = Math.floor(idx / PER_PAGE) + 1;
    if (targetPage !== page) setPage(targetPage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pulidoLink.openId, pieces, filtered]);

  // Si llegamos con ?action=create&suggestedSku=<sku> → abrir form de nueva pieza
  useEffect(() => {
    if (!puedeEditar) return; // roles sin edición de catálogo no crean piezas
    if (pulidoLink.action === "create" && pulidoLink.suggestedSku) {
      navigate(`new?suggestedSku=${encodeURIComponent(pulidoLink.suggestedSku)}`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pulidoLink.action, pulidoLink.suggestedSku]);

  // ── handlers ──────────────────────────────────────────────────────────
  const handleSave = async (id, draft) => {
    // PATCH endpoint lee _id desde req.body
    const payload = {
      _id:          id,
      name:         draft.name,
      details:      draft.details,
      sku:          draft.sku,
      skuAliases:   draft.skuAliases || [],
      material:     draft.material,
      geometry:     draft.geometry,
      materialPrice: draft.materialPrice || 0,
      price:        draft.price || 0,
      priceClient:  draft.priceClient || 0,
      // BOM (componentes del conjunto). El backend lo guarda como array de
      // { piece, quantity, notes } reemplazando el array entero.
      components:   (draft.components || []).map(c => ({
        piece:    c.piece?._id || c.piece,
        quantity: Number(c.quantity) > 0 ? Number(c.quantity) : 1,
        notes:    c.notes || "",
      })),
      stock:        draft.stock || 0,
      minStock:     draft.minStock || 0,
      reorderPoint: draft.reorderPoint || 0,
      blueprint:    draft.blueprint || null,
      // F5.8b — flags de calidad/origen
      requiereDosier: !!draft.requiereDosier,
      esImportada:    !!draft.esImportada,
      // D-Stock-Gestion — gestión de stock
      managedStock: !!draft.managedStock,
      materialRef:  draft.materialRef?._id || draft.materialRef || null,
      reorderQty:   draft.reorderQty || 0,
      consumoPorUnidad: draft.consumoPorUnidad || 1,
      // D-Corte — corte externo
      requiereCorteExterno: !!draft.requiereCorteExterno,
      proveedorCorte: draft.proveedorCorte?._id || draft.proveedorCorte?.entityId || draft.proveedorCorte || null,
      codigoCorte:   draft.codigoCorte || "",
      precioCorte:   draft.precioCorte || 0,
      planoCorte:    draft.planoCorte || null,
    };
    await backend.patch("piece", payload);
    setPieces(prev => prev.map(p => p._id === id ? { ...p, ...draft } : p));
  };

  const handleDelete = async () => {
    if (!deleteTarget?._id) return;
    // DELETE endpoint lee _id desde req.body
    await backend.delete("piece", { data: { _id: deleteTarget._id } });
    setPieces(prev => prev.filter(p => p._id !== deleteTarget._id));
    setDeleteTarget(null);
  };

  // ── métricas header ────────────────────────────────────────────────
  const topMetrics = useMemo(() => {
    const active        = pieces.filter(p => p.status !== "archived");
    const total         = active.length;
    const withP1        = active.filter(p => p.price > 0).length;
    const withP2        = active.filter(p => p.priceClient > 0).length;
    const outOfCatalog  = active.filter(p => p.notInOfficialCatalog).length;
    return { total, withP1, withP2, outOfCatalog };
  }, [pieces]);

  // ── columnas ──────────────────────────────────────────────────────────
  // Vista simple (15-sep-2026, pedido Maxi): solo código, nombre y precio; al hacer
  // clic en la fila se despliega toda la información abajo (igual que siempre).
  const [vistaSimple, setVistaSimple] = useState(() => { try { return localStorage.getItem("catalogo.vista") !== "completa"; } catch { return true; } });
  const toggleVista = () => setVistaSimple(v => { try { localStorage.setItem("catalogo.vista", v ? "completa" : "simple"); } catch {} return !v; });

  const columnsTodas = [
    {
      key: "sku", label: "SKU", width: 150,
      render: (_, row) => row.sku ? (
        <span className="bini-mono" style={{
          color: "#3498db", background: "#0d1e2e",
          padding: "2px 8px", borderRadius: 4,
          fontSize: 11, fontWeight: 500,
        }}>{row.sku}</span>
      ) : (
        <span style={{ fontSize: 10, color: "#999", fontStyle: "italic" }}>Sin SKU</span>
      ),
    },
    {
      key: "name", label: "Nombre",
      render: (_, row) => (
        <span>
          <span style={{ fontWeight: 500, color: "#fff" }}>{row.name || "—"}</span>
          {row.skuAliases?.length > 0 && (
            <span style={{
              marginLeft: 8, fontSize: 10, color: "#a78bfa",
              background: "#241a35", padding: "1px 6px", borderRadius: 3,
            }}>+{row.skuAliases.length} alias</span>
          )}
          {row.notInOfficialCatalog && (
            <span
              style={{
                marginLeft: 8, fontSize: 10, color: "#f39c12",
                background: "#2d2000", padding: "1px 6px", borderRadius: 3,
                border: "1px solid #4a3500",
              }}
              title="No aparece en el Drive 'Catalogo y Precios' — revisar"
            >⚠ fuera de catálogo</span>
          )}
          {row.details && (
            <span style={{ color: "#999", fontWeight: 400, marginLeft: 6, fontSize: 11 }}>
              — {row.details}
            </span>
          )}
        </span>
      ),
    },
    {
      key: "price", label: "Rocmin", width: 110, align: "right",
      render: (_, row) => (
        <span style={{ color: row.price > 0 ? "#2ecc71" : "#666", fontWeight: row.price > 0 ? 600 : 400 }}>
          {row.price > 0 ? `$${fmtN(row.price)}` : "—"}
        </span>
      ),
    },
    {
      key: "priceClient", label: "General", width: 110, align: "right",
      render: (_, row) => (
        <span style={{ color: row.priceClient > 0 ? "#3498db" : "#666", fontWeight: row.priceClient > 0 ? 600 : 400 }}>
          {row.priceClient > 0 ? `$${fmtN(row.priceClient)}` : "—"}
        </span>
      ),
    },
    {
      // P3 = neto desde tiempos de procesos (sin material, sin utilidad).
      // Calculado al vuelo desde piece.processes + piece.constants.prices.
      key: "p3", label: "Costo proceso", width: 120, align: "right",
      render: (_, row) => {
        const p3 = calcP3(row);
        return (
          <span style={{ color: p3 > 0 ? "#a78bfa" : "#666", fontWeight: p3 > 0 ? 600 : 400 }}>
            {p3 > 0 ? `$${fmtN(p3)}` : "—"}
          </span>
        );
      },
    },
  ];
  const columns = vistaSimple
    ? columnsTodas.filter(c => ["sku", "name", "price"].includes(c.key)).map(c => c.key === "price" ? { ...c, label: "Precio" } : c)
    : columnsTodas;;

  // ── renderDetail ──────────────────────────────────────────────────────
  const renderDetail = (data, helpers) => {
    const history     = historyByPiece[data._id];
    if (!helpers.isEditing && history === undefined) {
      loadHistory(data._id);
    }
    return (
      <PieceDrawerContent
        data={data}
        helpers={helpers}
        history={history}
        materials={materials}
        setPieces={setPieces}
        setDeleteTarget={setDeleteTarget}
        onOpenPriceDetail={(mode) => setPriceDetail({ piece: data, mode })}
        puedeEditar={puedeEditar}
      />
    );
  };

  // ── render ────────────────────────────────────────────────────────────
  return (
    <div style={{
      display: "block", padding: "clamp(12px, 2.5vw, 18px) clamp(10px, 3vw, 22px)", background: "#1a1a1a",
      minHeight: "100%", color: "#ddd", fontFamily: "Arial,sans-serif",
      fontSize: 13, textAlign: "left", boxSizing: "border-box", width: "100%",
    }}>

      {/* header */}
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8,
        alignItems: "center", marginBottom: 18 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>Catálogo de piezas</div>
        {puedeEditar && (
        <button
          onClick={() => navigate("new")}
          style={{ background: "#e30613", color: "#fff", border: "none",
            borderRadius: 6, padding: "7px 16px", fontSize: 12,
            fontWeight: 700, cursor: "pointer" }}
        >+ Nueva pieza</button>
        )}
      </div>

      {/* métricas — cards clickables (toggle filter exclusivo) */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <TopCard label="Total" value={topMetrics.total} color="#3498db"
          onClick={() => setFilter("all")} active={filter === "all"} />
        <TopCard label="Precios Rocmin" value={topMetrics.withP1} color="#2ecc71"
          onClick={() => setFilter(f => f === "withP1" ? "all" : "withP1")}
          active={filter === "withP1"} />
        <TopCard label="Precios Generales" value={topMetrics.withP2} color="#3498db"
          onClick={() => setFilter(f => f === "withP2" ? "all" : "withP2")}
          active={filter === "withP2"} />
        <TopCard label="Fuera catálogo" value={topMetrics.outOfCatalog} color="#f39c12"
          onClick={() => setFilter(f => f === "outOfCatalog" ? "all" : "outOfCatalog")}
          active={filter === "outOfCatalog"} />
      </div>

      {/* buscador + vista */}
      <div style={{ marginBottom: 14, display: "flex", gap: 8, alignItems: "center" }}>
        <input className="bini-input" style={{ flex: 1 }}
          placeholder="Buscar SKU, nombre, alias, material..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)} />
        <button type="button" onClick={toggleVista} className="chip-touch no-touch-min t-02"
          title={vistaSimple ? "Mostrar todas las columnas de precio" : "Solo código, nombre y precio"}
          style={{ background: "#1e1e1e", color: "#ddd", border: "1px solid #333", borderRadius: 6, padding: "8px 12px", fontSize: 11, cursor: "pointer", whiteSpace: "nowrap" }}>
          {vistaSimple ? "Vista completa" : "Vista simple"}
        </button>
      </div>

      {/* tabla */}
      <div style={{ border: "1px solid #2e2e2e", borderRadius: 8,
        overflow: "hidden", background: "#242424" }}>
        <ExpandableTable
          columns={columns}
          rows={pageRows}
          renderDetail={renderDetail}
          onSave={handleSave}
          emptyText={loading ? "Cargando..." : "Sin piezas con este filtro"}
          initialExpandedId={pulidoLink.openId}
          initialEditingId={pulidoLink.editingId}
        />
      </div>

      {/* paginación */}
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8,
          alignItems: "center", marginTop: 14, fontSize: 11, color: "#cbd5e1" }}>
          <div>
            Mostrando {((page - 1) * PER_PAGE) + 1}–{Math.min(page * PER_PAGE, filtered.length)} de {filtered.length}
          </div>
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="bini-btn-icon" style={{ opacity: page === 1 ? 0.4 : 1 }}>◂</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
              .map((p, i, arr) => (
                <React.Fragment key={p}>
                  {i > 0 && arr[i - 1] !== p - 1 && (
                    <span style={{ color: "#999", padding: "0 2px" }}>…</span>
                  )}
                  <button onClick={() => setPage(p)} className="bini-btn-icon"
                    style={{ background: p === page ? "#3498db" : "#2e2e2e",
                      color: p === page ? "#fff" : "#aaa", minWidth: 28 }}>
                    {p}
                  </button>
                </React.Fragment>
              ))}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="bini-btn-icon" style={{ opacity: page === totalPages ? 0.4 : 1 }}>▸</button>
          </div>
        </div>
      )}

      {/* modal eliminar */}
      <ConfirmModal
        open={!!deleteTarget}
        title={`Eliminar pieza ${deleteTarget?.name || ""}`}
        description={`SKU: ${deleteTarget?.sku || "—"}`}
        warning={{
          title: "Acción irreversible",
          description: "Si hay OTs que referencian esta pieza, quedarán con la referencia huérfana. Verificá el historial antes de eliminar.",
        }}
        confirmLabel="Eliminar pieza"
        confirmVariant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* modal detalle de precio (P1/P2/P3) */}
      {priceDetail && (
        <PriceDetailModal
          piece={priceDetail.piece}
          mode={priceDetail.mode}
          onClose={() => setPriceDetail(null)}
          onEditProcesses={() => {
            const pid = priceDetail.piece._id;
            setPriceDetail(null);
            navigate("/pieces/new", { state: { _id: pid } });
          }}
        />
      )}

      {/* ruta hija /pieces/new */}
      <Outlet />
    </div>
  );
};

// ── subcomponentes ─────────────────────────────────────────────────────────────

const MATERIAL_TYPES = ["Barra redonda","Barra cuadrada","Barra hexagonal","Plancha","Tubo","Perfil","Otro"];

// Selector de material vinculado + creación inline.
// Si se activa managedStock sin material, abre form auto. Permite crear un
// RawMaterial nuevo sin salir de /catálogo (queda persistente para otras piezas).
const MaterialPicker = ({ value, editing, materials, onChange, onMaterialCreated, pieceMaterialHint }) => {
  const currentId = value?._id || value || "";
  const [creating, setCreating] = useState(!currentId && editing && materials.length === 0);
  const [draft, setDraft] = useState({
    name: pieceMaterialHint || "", materialType: "", unit: "trozo", providerEntity: null,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const nameInputRef = useRef(null);

  // Crear proveedor inline si el usuario no lo tiene cargado
  const [showNewProvider, setShowNewProvider] = useState(false);
  const [newProvider, setNewProvider] = useState({ name: "", rut: "", email: "" });
  const [savingProvider, setSavingProvider] = useState(false);

  const handleSaveNewProvider = async () => {
    if (!newProvider.name.trim()) return;
    setSavingProvider(true);
    try {
      const res = await backend.post("entity", {
        ...newProvider,
        name: newProvider.name.trim(),
        type: "provider",
      });
      const ent = res.data;
      setDraft(d => ({
        ...d,
        providerEntity: { entityId: ent._id, name: ent.name, rut: ent.rut, type: ent.type },
      }));
      setShowNewProvider(false);
      setNewProvider({ name: "", rut: "", email: "" });
    } catch (e) {
      alert(e?.response?.data?.error || e?.response?.data || e.message);
    }
    setSavingProvider(false);
  };

  // Inserta un símbolo en la posición del cursor del input "Nombre".
  // Si el input no está enfocado, lo añade al final.
  const insertSymbolInName = (sym) => {
    const el = nameInputRef.current;
    const value = draft.name || "";
    if (!el || document.activeElement !== el) {
      setDraft(d => ({ ...d, name: value + sym }));
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end   = el.selectionEnd   ?? value.length;
    const next  = value.slice(0, start) + sym + value.slice(end);
    setDraft(d => ({ ...d, name: next }));
    // Restaurar foco + cursor después del símbolo (en el siguiente tick para que el value se aplique).
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + sym.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const handleSave = async () => {
    if (!draft.name.trim()) { setErr("Nombre obligatorio"); return; }
    setSaving(true); setErr(null);
    try {
      const res = await backend.post("rawMaterial", {
        name:         draft.name.trim(),
        materialType: draft.materialType || undefined,
        unit:         draft.unit || "trozo",
        provider:     draft.providerEntity?.name || undefined,
        stock:        0, // se ajusta después en /inventario
        minStock:     0,
      });
      onMaterialCreated(res.data);
      setCreating(false);
      setDraft({ name: "", materialType: "", unit: "trozo", providerEntity: null });
    } catch (e) {
      setErr(e?.response?.data || e.message);
    }
    setSaving(false);
  };

  if (!editing) {
    return (
      <div className="bini-field" style={{ marginTop: 10 }}>
        <label>Material a comprar al disparar la alerta</label>
        <div className="val">
          {value?.name || (
            <span style={{ color: "#e74c3c", fontStyle: "italic" }}>
              ⚠ Sin material vinculado — la alerta no podrá generar OC de compra
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bini-field" style={{ marginTop: 10 }}>
      <label>Material a comprar al disparar la alerta</label>

      {!creating && (
        <>
          {/* Sugerencias por match con material+geometry de la pieza */}
          {!currentId && pieceMaterialHint && (() => {
            const matches = findMaterialMatches(pieceMaterialHint, materials);
            const hasMatches = matches.length > 0;

            // Heurística para adivinar el tipo de material a partir del hint
            const guessType = (s) => {
              const t = s.toLowerCase();
              if (/diam|ø|⌀|redond/.test(t)) return "Barra redonda";
              if (/cuadrad|□/.test(t))        return "Barra cuadrada";
              if (/hexag/.test(t))            return "Barra hexagonal";
              if (/planch|plana/.test(t))     return "Plancha";
              if (/tubo|▢/.test(t))           return "Tubo";
              if (/perfil/.test(t))           return "Perfil";
              return "Otro";
            };

            // Crea RawMaterial al vuelo desde los datos legacy de la pieza y vincula
            const useThisMaterial = async () => {
              try {
                const res = await backend.post("rawMaterial", {
                  name:         pieceMaterialHint,
                  materialType: guessType(pieceMaterialHint),
                  unit:         "trozo",
                  stock:        0,
                  minStock:     0,
                });
                onMaterialCreated(res.data);
              } catch (e) {
                alert("Error al crear material: " + (e?.response?.data || e.message));
              }
            };

            return (
              <div style={{
                marginBottom: 8, padding: "8px 10px",
                background: "#0a1f15", border: "1px solid #1a3a25", borderRadius: 5,
              }}>
                {hasMatches ? (
                  <>
                    <div style={{ fontSize: 10, color: "#2ecc71", fontWeight: 600, marginBottom: 6 }}>
                      💡 Posibles coincidencias en materiales ya registrados:
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {matches.map(m => (
                        <button key={m._id} type="button"
                          onClick={() => onChange(m._id)}
                          style={{
                            background: "#0d2a15", color: "#2ecc71",
                            border: "1px solid #1a3a25", borderRadius: 4,
                            padding: "4px 10px", fontSize: 11, cursor: "pointer",
                            fontFamily: "Arial, sans-serif",
                          }}
                          title="Click para vincular este material a la pieza">
                          📦 {m.name}
                          {m.materialType && (
                            <span style={{ color: "#cbd5e1", marginLeft: 4 }}>· {m.materialType}</span>
                          )}
                        </button>
                      ))}
                    </div>
                    <div style={{ fontSize: 9, color: "#aaa", marginTop: 6, fontStyle: "italic" }}>
                      Detectadas por similitud con "{pieceMaterialHint}". Si ninguna sirve, usá el selector o "+ Nuevo material".
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 10, color: "#2ecc71", fontWeight: 600, marginBottom: 6 }}>
                      💡 Esta pieza tiene material cargado en el catálogo:
                    </div>
                    <button type="button" onClick={useThisMaterial}
                      style={{
                        background: "#0d2a15", color: "#2ecc71",
                        border: "1px solid #1a3a25", borderRadius: 4,
                        padding: "5px 12px", fontSize: 12, cursor: "pointer",
                        fontFamily: "Arial, sans-serif", fontWeight: 600,
                      }}
                      title="Crea el RawMaterial al vuelo desde los datos de la pieza y lo vincula">
                      📦 Usar "{pieceMaterialHint}" como material vinculado
                    </button>
                    <div style={{ fontSize: 9, color: "#aaa", marginTop: 6, fontStyle: "italic" }}>
                      Crea un material nuevo con esos datos en un click. Si querés afinarlo, usá "+ Nuevo material".
                    </div>
                  </>
                )}
              </div>
            );
          })()}

          <div style={{ display: "flex", gap: 6 }}>
            <select className="bini-input" style={{ flex: 1 }}
              value={currentId}
              onChange={e => onChange(e.target.value || null)}>
              <option value="">— Sin material vinculado —</option>
              {materials.map(m => (
                <option key={m._id} value={m._id}>
                  {m.name}{m.materialType ? ` · ${m.materialType}` : ""} (stock: {m.stock || 0} {m.unit || ""})
                </option>
              ))}
            </select>
            <button type="button" className="bini-btn-add"
              onClick={() => setCreating(true)}
              title="Crear un material nuevo y vincularlo">
              + Nuevo material
            </button>
          </div>
        </>
      )}

      {creating && (
        <div style={{ marginTop: 6, padding: 10, background: "#0d0d0d",
          border: "1px solid #2e2e2e", borderRadius: 6 }}>
          <div style={{ fontSize: 11, color: "#cbd5e1", marginBottom: 8 }}>
            Crear material y vincularlo a esta pieza. Quedará disponible para otras piezas.
          </div>
          <SimbolosToolbar
            compact
            targetLabel="se inserta en el campo Nombre"
            onInsert={sym => insertSymbolInName(sym)}
          />
          <div className="grid" style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
            <input className="bini-input" ref={nameInputRef} placeholder="Nombre (ej: SAE 4140 Ø2″ 150mm)"
              value={draft.name}
              onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} />
            <select className="bini-input" value={draft.materialType}
              onChange={e => setDraft(d => ({ ...d, materialType: e.target.value }))}>
              <option value="">Tipo…</option>
              {MATERIAL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <input className="bini-input" placeholder="Unidad (trozo, kg…)"
              value={draft.unit}
              onChange={e => setDraft(d => ({ ...d, unit: e.target.value }))} />
          </div>
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 10, color: "#cbd5e1", marginBottom: 4 }}>
              Proveedor habitual (opcional)
            </div>
            {!showNewProvider ? (
              <div style={{ display: "flex", gap: 6 }}>
                <div style={{ flex: 1 }}>
                  <EntityLookup
                    type="provider"
                    value={draft.providerEntity}
                    onChange={ent => setDraft(d => ({ ...d, providerEntity: ent }))}
                    placeholder="Buscar proveedor por nombre o RUT…"
                  />
                </div>
                <button type="button" className="bini-btn-add"
                  onClick={() => setShowNewProvider(true)}
                  title="Crear un proveedor nuevo">
                  + Nuevo
                </button>
              </div>
            ) : (
              <div style={{ padding: 8, background: "#161616",
                border: "1px solid #2e2e2e", borderRadius: 5 }}>
                <div style={{ fontSize: 10, color: "#cbd5e1", marginBottom: 6 }}>
                  Nuevo proveedor (queda en /contactos)
                </div>
                <div className="grid" style={{ display: "grid", gridTemplateColumns: "2fr 1fr 2fr", gap: 6, marginBottom: 6 }}>
                  <input className="bini-input" placeholder="Nombre / razón social *"
                    value={newProvider.name}
                    onChange={e => setNewProvider(p => ({ ...p, name: e.target.value }))} />
                  <input className="bini-input" placeholder="RUT"
                    value={newProvider.rut}
                    onChange={e => setNewProvider(p => ({ ...p, rut: maskRut(e.target.value) }))} />
                  <input className="bini-input" placeholder="Email (opcional)"
                    value={newProvider.email}
                    onChange={e => setNewProvider(p => ({ ...p, email: e.target.value }))} />
                </div>
                <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                  <button type="button" className="bini-btn bini-btn-cancel"
                    onClick={() => { setShowNewProvider(false); setNewProvider({ name: "", rut: "", email: "" }); }}>
                    Cancelar
                  </button>
                  <button type="button" className="bini-btn bini-btn-save"
                    onClick={handleSaveNewProvider}
                    disabled={savingProvider || !newProvider.name.trim()}>
                    {savingProvider ? "Guardando…" : "Crear proveedor"}
                  </button>
                </div>
              </div>
            )}
          </div>
          {err && <div style={{ color: "#e74c3c", fontSize: 11, marginBottom: 6 }}>{err}</div>}
          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
            <button type="button" className="bini-btn bini-btn-cancel"
              onClick={() => { setCreating(false); setErr(null); }}>
              Cancelar
            </button>
            <button type="button" className="bini-btn bini-btn-save"
              onClick={handleSave} disabled={saving || !draft.name.trim()}>
              {saving ? "Guardando…" : "Crear y vincular"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Match fuzzy: tokeniza hint y materiales, devuelve materials con score > 0
// ordenados por mejor coincidencia. Usado para sugerir materiales existentes
// cuando una pieza activa managedStock y ya tiene material/geometry cargados.
const tokenize = s => String(s || "").toLowerCase()
  .replace(/[^a-z0-9ø½¼⅛²³]+/g, " ")
  .split(/\s+/)
  .filter(t => t.length >= 2);

const findMaterialMatches = (hint, materials) => {
  const hintTokens = tokenize(hint);
  if (hintTokens.length === 0) return [];
  return materials
    .map(m => {
      const mTokens = new Set(tokenize(`${m.name || ""} ${m.materialType || ""}`));
      const score = hintTokens.filter(t => mTokens.has(t)).length;
      return { m, score };
    })
    .filter(x => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map(x => x.m);
};

// D-Corte — Bloque "Corte externo" para piezas que se mandan a cortar a un
// proveedor con precio acordado (sin cotización). El usuario activa/desactiva
// por pieza. Cuando activo: proveedor + código interno único + precio + plano.
const CorteExternoBlock = ({ data, editing, onUpdate, pieceId, onPersisted }) => {
  const active = !!data.requiereCorteExterno;

  // Auto-persistir el proveedor de corte al seleccionarlo (similar a materialRef).
  // Los demás campos (código, precio, plano) se guardan con "Guardar cambios".
  const handleProveedorChange = async (ent) => {
    onUpdate(d => ({ ...d, proveedorCorte: ent }));
    if (!ent?.entityId) return;
    try {
      await backend.patch("piece", { _id: pieceId, proveedorCorte: ent.entityId });
      onPersisted?.({ proveedorCorte: ent });
    } catch (e) {
      console.error("[Pieces] Auto-save proveedorCorte fail:", e);
    }
  };

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{
        fontSize: 11, fontWeight: 600, color: "#cbd5e1",
        textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 8,
      }}>
        Corte externo
      </div>

      <label style={{
        display: "flex", alignItems: "center", gap: 6, fontSize: 12,
        color: active ? "#a78bfa" : "#aaa",
        cursor: editing ? "pointer" : "default",
        opacity: editing ? 1 : 0.85, marginBottom: 10,
      }}>
        <input
          type="checkbox"
          checked={active}
          disabled={!editing}
          onChange={e => onUpdate(d => ({ ...d, requiereCorteExterno: e.target.checked }))}
        />
        ✂ Esta pieza se manda a cortar a un proveedor externo
        <span style={{ fontSize: 10, color: "#999", marginLeft: 4 }}>
          (precio acordado, no requiere cotizar)
        </span>
      </label>

      {active && (
        <>
          {editing && (
            <div style={{
              marginBottom: 10, padding: "6px 10px",
              background: "#1a1500", border: "1px solid #4a3500",
              borderRadius: 5, fontSize: 11, color: "#f39c12",
            }}>
              ⚠ Recordá hacer click en <strong>"Guardar cambios"</strong> para persistir código / precio / plano. (El proveedor se guarda solo).
            </div>
          )}

          <div className="bini-field" style={{ marginBottom: 10 }}>
            <label>Proveedor de corte</label>
            {editing ? (
              <EntityLookup
                type="provider"
                value={data.proveedorCorte ? {
                  entityId: data.proveedorCorte._id || data.proveedorCorte,
                  name: data.proveedorCorte.name,
                  rut: data.proveedorCorte.rut,
                } : null}
                onChange={handleProveedorChange}
                placeholder="Buscar proveedor de corte…"
              />
            ) : (
              <div className="val">
                {data.proveedorCorte?.name || (
                  <span style={{ color: "#e74c3c", fontStyle: "italic" }}>Sin proveedor asignado</span>
                )}
              </div>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10, marginBottom: 10 }}>
            <div className="bini-field">
              <label>Código interno del plano</label>
              {editing ? (
                <input className="bini-input bini-mono"
                  value={data.codigoCorte || ""}
                  onChange={e => onUpdate(d => ({ ...d, codigoCorte: e.target.value }))}
                  placeholder="Ej: BIN-CRT-042" />
              ) : (
                <div className="val bini-mono">
                  {data.codigoCorte || <span style={{ color: "#aaa", fontStyle:"italic" }}>—</span>}
                </div>
              )}
              <div style={{ fontSize: 10, color: "#999", marginTop: 3 }}>
                El proveedor identifica el plano por este código (debe ser único).
              </div>
            </div>
            <div className="bini-field">
              <label>Precio acordado / unidad ($)</label>
              {editing ? (
                <input type="number" onFocus={e => e.target.select()} min={0} className="bini-input"
                  value={data.precioCorte || 0}
                  onChange={e => onUpdate(d => ({ ...d, precioCorte: Number(e.target.value) }))} />
              ) : (
                <div className="val">{data.precioCorte > 0 ? `$${Number(data.precioCorte).toLocaleString("es-CL")}` : "—"}</div>
              )}
            </div>
          </div>

          <div className="bini-field">
            <label>Plano para enviar al proveedor</label>
            <div style={{ fontSize: 10, color: "#999", marginBottom: 6 }}>
              Plano "camuflado" o de corte que se le entrega al proveedor. Distinto del plano técnico interno (arriba).
            </div>
            <BlueprintEditor
              blueprint={data.planoCorte}
              editable={editing}
              onChange={newPc => onUpdate(d => ({ ...d, planoCorte: newPc }))}
            />
          </div>
        </>
      )}
    </div>
  );
};

const TopCard = ({ label, value, color, onClick, active }) => (
  <div onClick={onClick} style={{
    flex: "1 1 0", padding: "10px 14px",
    background: active ? `${color}22` : "#242424",
    border: `1px solid ${active ? color : "#2e2e2e"}`,
    borderRadius: 6, cursor: onClick ? "pointer" : "default",
  }}>
    <div style={{ fontSize: 9, color: "#999", textTransform: "uppercase", letterSpacing: ".05em" }}>
      {label}
    </div>
    <div style={{ fontSize: 22, fontWeight: 700, color, marginTop: 2 }}>{value}</div>
  </div>
);

const MetricCard = ({ label, value, sub, color, small }) => (
  <div style={{ padding: "8px 12px", background: "#0d0d0d",
    border: "1px solid #2e2e2e", borderRadius: 6 }}>
    <div style={{ fontSize: 9, color: "#999", textTransform: "uppercase", letterSpacing: ".05em" }}>
      {label}
    </div>
    <div style={{ fontSize: small ? 12 : 18, fontWeight: 600, color, marginTop: 3,
      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
      {value}
    </div>
    {sub && <div style={{ fontSize: 9, color: "#999", marginTop: 2 }}>{sub}</div>}
  </div>
);

const SkuAliasesEditor = ({ aliases, editable, onChange }) => {
  const [inputValue, setInputValue] = useState("");

  const addAlias = () => {
    const v = inputValue.trim();
    if (!v || aliases.includes(v)) { setInputValue(""); return; }
    onChange([...aliases, v]);
    setInputValue("");
  };

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {aliases.length === 0 && !editable && (
          <span style={{ color: "#999", fontSize: 11 }}>Sin alias registrados</span>
        )}
        {aliases.map((alias, idx) => (
          <span key={idx} style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "3px 9px", fontSize: 11,
            background: "#241a35", border: "1px solid #3d2659",
            color: "#a78bfa", borderRadius: 4, fontFamily: "monospace",
          }}>
            {alias}
            {editable && (
              <button type="button"
                onClick={() => onChange(aliases.filter((_, i) => i !== idx))}
                style={{ background: "none", border: "none", color: "#a78bfa",
                  cursor: "pointer", padding: 0, fontSize: 14, lineHeight: 1 }}>
                ×
              </button>
            )}
          </span>
        ))}
      </div>
      {editable && (
        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
          <input className="bini-input bini-mono" value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addAlias(); } }}
            placeholder="Agregar SKU alternativo (ej: ROC-3389)"
            style={{ flex: 1 }} />
          <button type="button" className="bini-btn-add"
            onClick={addAlias} disabled={!inputValue.trim()}>
            + Agregar
          </button>
        </div>
      )}
    </div>
  );
};

const BlueprintEditor = ({ blueprint, editable, onChange }) => {
  const [showInput, setShowInput]   = useState(false);
  const [linkInput, setLinkInput]   = useState(blueprint?.url || "");

  const handleSaveLink = () => {
    const url = linkInput.trim();
    if (!url) { onChange(null); setShowInput(false); return; }
    const gMatch = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/) || url.match(/id=([a-zA-Z0-9_-]+)/);
    const id = gMatch ? gMatch[1] : null;
    onChange({ url, id: id || undefined, source: id ? "gdrive" : "external" });
    setShowInput(false);
  };

  if (blueprint?.url && !showInput) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8,
        padding: "8px 10px", background: "#0d0d0d",
        border: "1px solid #2e2e2e", borderRadius: 6, marginBottom: 6 }}>
        <span style={{ fontSize: 16 }}>📐</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, color: "#ddd" }}>
            Plano técnico
            {blueprint.source === "gdrive" && (
              <span style={{ marginLeft: 6, fontSize: 9, padding: "1px 5px",
                background: "#1a2e1a", color: "#5ee06a", borderRadius: 3 }}>Google Drive</span>
            )}
          </div>
          <div style={{ fontSize: 10, color: "#999", wordBreak: "break-all", marginTop: 2 }}>
            {blueprint.url}
          </div>
        </div>
        <a href={blueprint.url} target="_blank" rel="noopener noreferrer"
          className="bini-btn-icon" style={{ textDecoration: "none" }}>Abrir</a>
        {editable && <>
          <button type="button" className="bini-btn-icon"
            onClick={() => { setLinkInput(blueprint.url); setShowInput(true); }}>✎</button>
          <button type="button" className="bini-btn-icon danger"
            onClick={() => onChange(null)}>✕</button>
        </>}
      </div>
    );
  }

  if (!blueprint?.url && !editable) {
    return (
      <div style={{ color: "#999", fontSize: 11, paddingBottom: 6 }}>
        Sin plano de Google Drive — usá el DocumentAttacher abajo para subir PDFs.
      </div>
    );
  }

  if (editable) {
    return (
      <div style={{ marginBottom: 6 }}>
        {blueprint?.url && !showInput && null}
        {!blueprint?.url && !showInput && (
          <button type="button" className="bini-btn-icon"
            onClick={() => setShowInput(true)}
            style={{ marginBottom: 6 }}>
            + Link Google Drive
          </button>
        )}
        {showInput && (
          <div style={{ padding: 10, background: "#0d0d0d",
            border: "1px solid #2e2e2e", borderRadius: 6 }}>
            <div className="bini-field" style={{ marginBottom: 8 }}>
              <label>URL del plano</label>
              <input className="bini-input bini-mono" value={linkInput}
                onChange={e => setLinkInput(e.target.value)}
                placeholder="https://drive.google.com/file/d/..." />
            </div>
            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
              <button type="button" className="bini-btn bini-btn-cancel"
                onClick={() => { setShowInput(false); setLinkInput(blueprint?.url || ""); }}>
                Cancelar
              </button>
              <button type="button" className="bini-btn bini-btn-save"
                onClick={handleSaveLink}>Guardar link</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

// ─── PieceDrawerContent ────────────────────────────────────────────────────
// Drawer del expandible reorganizado en 3 tabs:
//   - Resumen:       precios (clickables), chips de estado, detalles, alias, mini-historial.
//   - Configuración: geometría, gestión de stock, corte externo, plano, dossier/importada.
//   - Historial:     métricas + tabla completa de fabricación.
// El estado del tab es local (se resetea al colapsar/expandir otra fila).
const PieceDrawerContent = ({ data, helpers, history, materials, setPieces, setDeleteTarget, onOpenPriceDetail, puedeEditar }) => {
  const [tab, setTab] = useState("resumen");
  const [promoting, setPromoting] = useState(false);
  const [promoteError, setPromoteError] = useState("");
  const isEditing = helpers.isEditing;
  const metrics     = history?.metrics;
  const historyRows = history?.rows || [];
  const p3 = calcP3(data);

  const promote = async (event) => {
    event.stopPropagation();
    setPromoting(true);
    setPromoteError("");
    try {
      const { data: promoted } = await backend.patch(`piece/${data._id}/promote`);
      setPieces(prev => prev.map(piece => piece._id === data._id ? { ...piece, ...promoted } : piece));
    } catch (err) {
      setPromoteError(err?.response?.data?.error || "No se pudo promover la pieza");
    } finally {
      setPromoting(false);
    }
  };

  return (
    <div style={{ padding: "14px 18px" }}>
      {/* header */}
      <div className="bini-expand-header">
        <div>
          <div className="bini-expand-title">{data.name || "Sin nombre"}</div>
          <div className="bini-expand-sub">
            <span className="bini-mono" style={{ color: "#3498db" }}>{data.sku || "—"}</span>
            {(data.materialRef?.name || data.material) && <span> · {data.materialRef?.name || data.material}</span>}
            {data.geometry && <span> · {data.geometry}</span>}
          </div>
        </div>
        <div className="bini-expand-actions">
          {/* 28-ago (roles): editar/eliminar piezas solo admin/operator —
              el resto consulta precios sin acciones de escritura. */}
          {!isEditing && puedeEditar && (
            <>
              <button className="bini-btn bini-btn-edit"
                onClick={e => { e.stopPropagation(); helpers.startEdit(); }}>✎ Editar</button>
              <button className="bini-btn-icon danger"
                onClick={e => { e.stopPropagation(); setDeleteTarget(data); }}>Eliminar</button>
            </>
          )}
        </div>
      </div>

      {data.temporary && (
        <div style={{
          marginTop: 10, padding: "8px 10px", borderRadius: 5,
          border: "1px solid rgba(243,156,18,.4)", background: "rgba(243,156,18,.08)",
          display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        }}>
          <span style={{ color: "#f39c12", fontSize: 11, flex: 1 }}>
            ⏱ Pieza temporal. Expira {data.expiresAt ? fmt(data.expiresAt) : "90 días después de creada"} y luego se archivará automáticamente.
          </span>
          {puedeEditar && (
            <button className="bini-btn bini-btn-save" disabled={promoting} onClick={promote}>
              {promoting ? "Promoviendo…" : "Promover al catálogo"}
            </button>
          )}
          {promoteError && <span style={{ color: "#e74c3c", fontSize: 10, width: "100%" }}>{promoteError}</span>}
        </div>
      )}

      {/* tabs */}
      <div style={{ display: "flex", gap: 2, marginTop: 16, borderBottom: "1px solid #2e2e2e" }}>
        {[
          { id: "resumen",     label: "Resumen" },
          { id: "config",      label: "Configuración" },
          { id: "historial",   label: "Historial" },
        ].map(t => (
          <button key={t.id}
            onClick={e => { e.stopPropagation(); setTab(t.id); }}
            style={{
              background: "transparent", border: "none", padding: "8px 16px",
              color: tab === t.id ? "#3498db" : "#888",
              fontSize: 12, fontWeight: tab === t.id ? 700 : 500,
              borderBottom: tab === t.id ? "2px solid #3498db" : "2px solid transparent",
              cursor: "pointer", marginBottom: -1,
            }}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ marginTop: 14 }}>
        {tab === "resumen"   && (
          <ResumenTab data={data} isEditing={isEditing} helpers={helpers} p3={p3}
            onOpenPriceDetail={onOpenPriceDetail} historyRows={historyRows} metrics={metrics} />
        )}
        {tab === "config"    && (
          <ConfigTab data={data} isEditing={isEditing} helpers={helpers}
            materials={materials} setPieces={setPieces} />
        )}
        {tab === "historial" && (
          <HistorialTab data={data} history={history} metrics={metrics} historyRows={historyRows} />
        )}
      </div>

      {/* action bar modo edición */}
      {isEditing && (
        <div className="bini-action-bar" style={{ marginTop: 14 }}>
          <div />
          <div className="bini-action-bar-r">
            <button className="bini-btn bini-btn-cancel"
              onClick={e => { e.stopPropagation(); helpers.cancelEdit(); }}>Cancelar</button>
            <button className="bini-btn bini-btn-save" disabled={helpers.isSaving}
              onClick={e => { e.stopPropagation(); helpers.saveEdit(); }}>
              {helpers.isSaving ? "Guardando…" : "Guardar cambios"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── ResumenTab ────────────────────────────────────────────────────────────
const ResumenTab = ({ data, isEditing, helpers, p3, onOpenPriceDetail, historyRows, metrics }) => (
  <>
    {/* identidad */}
    <div className="grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
      <div className="bini-field">
        <label>Nombre</label>
        {isEditing ? (
          <input className="bini-input" name="name" value={data.name || ""}
            onChange={e => helpers.updateDraft(d => ({ ...d, name: e.target.value }))} />
        ) : <div className="val">{data.name || "—"}</div>}
      </div>
      <div className="bini-field">
        <label>SKU</label>
        {isEditing ? (
          <input className="bini-input bini-mono" name="sku" value={data.sku || ""}
            onChange={e => helpers.updateDraft(d => ({ ...d, sku: e.target.value }))} />
        ) : <div className="val bini-mono">{data.sku || "—"}</div>}
      </div>
      <div className="bini-field">
        <label>Material{data.materialRef && <span style={{ marginLeft: 6, fontSize: 9, color: "#2ecc71" }}>📦 sincronizado</span>}</label>
        {data.materialRef ? (
          <div className="val" style={{ color: "#aaa" }}>
            {data.materialRef.name}
            {data.materialRef.materialType && <span style={{ color: "#aaa" }}> · {data.materialRef.materialType}</span>}
          </div>
        ) : isEditing ? (
          <input className="bini-input" name="material" value={data.material || ""}
            onChange={e => helpers.updateDraft(d => ({ ...d, material: e.target.value }))}
            placeholder="Ej: SAE 4140" />
        ) : <div className="val">{data.material || "—"}</div>}
      </div>
    </div>

    {/* Precios — 3 cards clickables */}
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#cbd5e1", textTransform: "uppercase",
        letterSpacing: ".05em", marginBottom: 8 }}>Precios</div>
      <div className="grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        <PriceCard label="Rocmin" value={data.price} color="#2ecc71"
          isEditing={isEditing} editKey="price" helpers={helpers}
          onClick={() => onOpenPriceDetail("P1")} />
        <PriceCard label="General" value={data.priceClient} color="#3498db"
          isEditing={isEditing} editKey="priceClient" helpers={helpers}
          onClick={() => onOpenPriceDetail("P2")} />
        <PriceCard label="Costo proceso" value={p3} color="#a78bfa"
          isEditing={false} computed
          onClick={() => onOpenPriceDetail("P3")} />
      </div>
      <div style={{ fontSize: 10, color: "#999", marginTop: 6 }}>
        Click en cada precio para ver el historial / desglose del cálculo.
      </div>
    </div>

    {/* Chips de estado */}
    <div style={{ marginTop: 16, display: "flex", gap: 6, flexWrap: "wrap" }}>
      <StatusChip on={(data.components || []).length > 0}
        label={`Conjunto (${(data.components || []).length})`}
        icon="🧩" colorOn="#a78bfa" />
      <StatusChip on={data.managedStock} label={`Stock ${data.stock ?? 0}`} icon="📦" colorOn="#2ecc71" />
      <StatusChip on={!!data.blueprint?.url} label="Plano" icon="📐" colorOn="#3498db" />
      <StatusChip on={data.requiereDosier} label="Dossier" icon="📋" colorOn="#f39c12" />
      <StatusChip on={data.esImportada} label="Importada" icon="🌐" colorOn="#3498db" />
      <StatusChip on={data.requiereCorteExterno} label="Corte ext." icon="✂" colorOn="#f39c12" />
      <StatusChip on={data.notInOfficialCatalog} label="Fuera catálogo" icon="⚠" colorOn="#f39c12" />
    </div>

    {/* Detalles adicionales */}
    <div className="bini-field" style={{ marginTop: 14 }}>
      <label>Detalles adicionales</label>
      {isEditing ? (
        <input className="bini-input" name="details" value={data.details || ""}
          onChange={e => helpers.updateDraft(d => ({ ...d, details: e.target.value }))}
          placeholder="Ej: Para máquina perforadora Atlas Copco..." />
      ) : <div className="val">{data.details || "—"}</div>}
    </div>

    {/* SKU Aliases */}
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#cbd5e1", textTransform: "uppercase",
        letterSpacing: ".05em", marginBottom: 4 }}>
        SKUs alternativos ({data.skuAliases?.length || 0})
      </div>
      <div style={{ fontSize: 10, color: "#999", marginBottom: 8 }}>
        Códigos con los que clientes piden esta pieza (Rocmin, Codelco, etc.)
      </div>
      <SkuAliasesEditor aliases={data.skuAliases || []} editable={isEditing}
        onChange={newAliases => helpers.updateDraft(d => ({ ...d, skuAliases: newAliases }))} />
    </div>

    {/* mini-historial — últimas 3 OTs */}
    {!isEditing && metrics?.workOrdersCount > 0 && (
      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "#cbd5e1", textTransform: "uppercase",
          letterSpacing: ".05em", marginBottom: 8 }}>
          Últimas OTs ({metrics.workOrdersCount} totales — ver completo en tab Historial)
        </div>
        <table className="bini-items-table">
          <thead>
            <tr>
              <th style={{ width: 85 }}>Fecha</th>
              <th style={{ width: 80 }}>N° OT</th>
              <th>Cliente</th>
              <th style={{ width: 55, textAlign: "right" }}>Cant</th>
              <th style={{ width: 100, textAlign: "right" }}>P. unit</th>
            </tr>
          </thead>
          <tbody>
            {historyRows.slice(0, 3).map(r => (
              <tr key={r._id}>
                <td style={{ color: "#cbd5e1", fontSize: 11 }}>{r.date ? new Date(r.date).toLocaleDateString("es-CL", {day:"2-digit",month:"short",year:"2-digit"}) : "—"}</td>
                <td><span className="bini-mono" style={{ color: "#3498db", fontWeight: 600 }}>{r.number}</span></td>
                <td style={{ color: "#ccc" }}>{r.client?.name || "—"}</td>
                <td style={{ textAlign: "right" }}>{r.quantity}</td>
                <td style={{ textAlign: "right" }}>${fmtN(r.pUnit)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </>
);

// ─── ConfigTab ─────────────────────────────────────────────────────────────
const ConfigTab = ({ data, isEditing, helpers, materials, setPieces }) => (
  <>
    {/* Geometría y costo material */}
    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10 }}>
      <div className="bini-field">
        <label>Geometría / dimensiones</label>
        {isEditing ? (
          <input className="bini-input" name="geometry" value={data.geometry || ""}
            onChange={e => helpers.updateDraft(d => ({ ...d, geometry: e.target.value }))}
            placeholder="Ej: Ø50mm, largo 200mm" />
        ) : <div className="val">{data.geometry || "—"}</div>}
      </div>
      <div className="bini-field">
        <label>Costo material ($)</label>
        {isEditing ? (
          <input type="number" onFocus={e => e.target.select()} min={0} className="bini-input" name="materialPrice" value={data.materialPrice || 0}
            onChange={e => helpers.updateDraft(d => ({ ...d, materialPrice: Number(e.target.value) }))} />
        ) : <div className="val">{data.materialPrice > 0 ? `$${fmtN(data.materialPrice)}` : "—"}</div>}
      </div>
    </div>

    {/* Clasificación */}
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#cbd5e1", textTransform: "uppercase",
        letterSpacing: ".05em", marginBottom: 8 }}>Clasificación</div>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12,
          color: data.requiereDosier ? "#f39c12" : "#aaa",
          cursor: isEditing ? "pointer" : "default", opacity: isEditing ? 1 : 0.85 }}>
          <input type="checkbox" checked={!!data.requiereDosier} disabled={!isEditing}
            onChange={e => helpers.updateDraft(d => ({ ...d, requiereDosier: e.target.checked }))} />
          📋 Requiere dossier de calidad
          <span style={{ fontSize: 10, color: "#999", marginLeft: 4 }}>(estructural / certificados de material)</span>
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12,
          color: data.esImportada ? "#3498db" : "#aaa",
          cursor: isEditing ? "pointer" : "default", opacity: isEditing ? 1 : 0.85 }}>
          <input type="checkbox" checked={!!data.esImportada} disabled={!isEditing}
            onChange={e => helpers.updateDraft(d => ({ ...d, esImportada: e.target.checked }))} />
          🌐 Pieza importada
          <span style={{ fontSize: 10, color: "#999", marginLeft: 4 }}>(comprada hecha — no requiere control dimensional)</span>
        </label>
      </div>
    </div>

    {/* Gestión de stock */}
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#cbd5e1", textTransform: "uppercase",
        letterSpacing: ".05em", marginBottom: 8 }}>Gestión de stock</div>
      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12,
        color: data.managedStock ? "#2ecc71" : "#aaa",
        cursor: isEditing ? "pointer" : "default", opacity: isEditing ? 1 : 0.85, marginBottom: 10 }}>
        <input type="checkbox" checked={!!data.managedStock} disabled={!isEditing}
          onChange={e => helpers.updateDraft(d => ({ ...d, managedStock: e.target.checked }))} />
        📦 Gestionar stock de esta pieza
        <span style={{ fontSize: 10, color: "#999", marginLeft: 4 }}>(aparece en /inventario y dispara alerta de compra)</span>
      </label>
      {data.managedStock && (
        <>
          {isEditing && (
            <div style={{ marginBottom: 10, padding: "6px 10px", background: "#1a1500",
              border: "1px solid #4a3500", borderRadius: 5, fontSize: 11, color: "#f39c12" }}>
              ⚠ Recordá hacer click en <strong>"Guardar cambios"</strong> al final del panel para persistir stock / mín / reponer / cantidad.
            </div>
          )}
          <div className="grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
            <div className="bini-field">
              <label>Stock actual</label>
              {isEditing ? (
                <input type="number" onFocus={e => e.target.select()} min={0} className="bini-input"
                  value={data.stock || 0}
                  onChange={e => helpers.updateDraft(d => ({ ...d, stock: Number(e.target.value) }))} />
              ) : <div className="val">{data.stock || 0} un</div>}
            </div>
            <div className="bini-field">
              <label>Mantener (mín.)</label>
              {isEditing ? (
                <input type="number" onFocus={e => e.target.select()} min={0} className="bini-input"
                  value={data.minStock || 0}
                  onChange={e => helpers.updateDraft(d => ({ ...d, minStock: Number(e.target.value) }))} />
              ) : <div className="val">{data.minStock || 0} un</div>}
            </div>
            <div className="bini-field">
              <label>Reponer cuando queden</label>
              {isEditing ? (
                <input type="number" onFocus={e => e.target.select()} min={0} className="bini-input"
                  value={data.reorderPoint || 0}
                  onChange={e => helpers.updateDraft(d => ({ ...d, reorderPoint: Number(e.target.value) }))} />
              ) : <div className="val">{data.reorderPoint || 0} un</div>}
            </div>
            <div className="bini-field">
              <label>Cantidad a pedir</label>
              {isEditing ? (
                <input type="number" onFocus={e => e.target.select()} min={0} className="bini-input"
                  value={data.reorderQty || 0}
                  onChange={e => helpers.updateDraft(d => ({ ...d, reorderQty: Number(e.target.value) }))} />
              ) : <div className="val">{data.reorderQty || 0}</div>}
            </div>
          </div>
          {data.materialRef && (
            <div className="bini-field" style={{ marginTop: 10 }}>
              <label>Material consumido por unidad fabricada</label>
              {isEditing ? (
                <input type="number" onFocus={e => e.target.select()} min={0} step={0.01} className="bini-input"
                  style={{ maxWidth: 220 }} value={data.consumoPorUnidad ?? 1}
                  onChange={e => helpers.updateDraft(d => ({ ...d, consumoPorUnidad: Number(e.target.value) || 0 }))} />
              ) : (
                <div className="val">{data.consumoPorUnidad ?? 1} {data.materialRef.unit || "un"} de {data.materialRef.name}</div>
              )}
              <div style={{ fontSize: 10, color: "#999", marginTop: 3 }}>
                Cuántas unidades de <strong>{data.materialRef.name || "el material"}</strong> consume cada {data.name || "pieza"} fabricada. Default 1.
              </div>
            </div>
          )}
          <MaterialPicker
            value={data.materialRef} editing={isEditing} materials={materials}
            onChange={ref => helpers.updateDraft(d => ({ ...d, materialRef: ref }))}
            onMaterialCreated={async (created) => {
              helpers.updateDraft(d => ({ ...d, materialRef: created }));
              try {
                await backend.patch("piece", { _id: data._id, materialRef: created._id });
                setPieces(prev => prev.map(p => p._id === data._id ? { ...p, materialRef: created } : p));
              } catch (e) { console.error("[Pieces] Auto-save materialRef fail:", e); }
            }}
            pieceMaterialHint={[data.material, data.geometry].filter(Boolean).join(" ")}
          />
        </>
      )}
    </div>

    {/* Corte externo */}
    <CorteExternoBlock data={data} editing={isEditing}
      onUpdate={updater => helpers.updateDraft(updater)} pieceId={data._id}
      onPersisted={updates => setPieces(prev => prev.map(p => p._id === data._id ? { ...p, ...updates } : p))} />

    {/* Plano técnico */}
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#cbd5e1", textTransform: "uppercase",
        letterSpacing: ".05em", marginBottom: 8 }}>Plano técnico</div>
      <BlueprintEditor blueprint={data.blueprint} editable={isEditing}
        onChange={newBp => helpers.updateDraft(d => ({ ...d, blueprint: newBp }))} />
      <DocumentAttacher refField="pieceRef" refId={data._id} defaultType="planoTecnico" />
    </div>

    {/* Componentes (BOM) */}
    <ComponentsEditor data={data} isEditing={isEditing} helpers={helpers} />
  </>
);

// ─── ComponentsEditor (BOM) ────────────────────────────────────────────────
// Define qué piezas componen un conjunto. Si components.length > 0, la pieza
// se considera un "conjunto" y la UI lo marca con el chip 🧩 en el Resumen.
// El precio del conjunto es independiente — la suma de componentes se muestra
// como referencia y se calcula gap (markup / descuento del armado).
const ComponentsEditor = ({ data, isEditing, helpers }) => {
  const components = data.components || [];
  const suma = sumComponentes(data);
  const precio = Number(data.price) || 0;
  const gap = precio - suma;
  const gapPct = suma > 0 ? (gap / suma) * 100 : 0;

  const updateAt = (idx, patch) => {
    helpers.updateDraft(d => {
      const next = [...(d.components || [])];
      next[idx] = { ...next[idx], ...patch };
      return { ...d, components: next };
    });
  };
  const removeAt = (idx) => {
    helpers.updateDraft(d => ({
      ...d,
      components: (d.components || []).filter((_, i) => i !== idx),
    }));
  };
  const addEmpty = () => {
    helpers.updateDraft(d => ({
      ...d,
      components: [...(d.components || []), { piece: null, quantity: 1, notes: "" }],
    }));
  };

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: ".05em" }}>
          🧩 Componentes (BOM) — {components.length}
        </div>
        {components.length > 0 && (
          <div style={{ fontSize: 11, color: "#cbd5e1" }}>
            Σ componentes: <span style={{ color: "#aaa", fontWeight: 600 }}>${fmtN(suma)}</span>
            {precio > 0 && (
              <>
                {" · Precio conjunto: "}
                <span style={{ color: "#2ecc71", fontWeight: 600 }}>${fmtN(precio)}</span>
                {" · Δ: "}
                <span style={{
                  color: Math.abs(gapPct) > 10 ? "#f39c12" : "#666",
                  fontWeight: 600,
                }}>
                  {gap >= 0 ? "+" : ""}{fmtN(gap)} ({gapPct.toFixed(1)}%)
                </span>
              </>
            )}
          </div>
        )}
      </div>
      <div style={{ fontSize: 10, color: "#999", marginBottom: 8 }}>
        Si esta pieza es un conjunto (HOUSE, BLOW DOWN VALVE, SWIVEL, etc.), listá acá las piezas que la componen y la cantidad. Cuando un cliente compre el conjunto, producción va a ver los componentes como sub-tareas (Fase 3).
      </div>

      {components.length === 0 && !isEditing && (
        <div style={{ fontSize: 11, color: "#999", padding: "8px 0",
          borderTop: "1px dashed #2e2e2e" }}>
          Esta pieza no es un conjunto (no tiene componentes definidos).
        </div>
      )}

      {components.length > 0 && (
        <table className="bini-items-table" style={{ marginBottom: 8 }}>
          <thead>
            <tr>
              <th>Componente</th>
              <th style={{ width: 70, textAlign: "right" }}>Qty</th>
              <th style={{ width: 110, textAlign: "right" }}>P. unit</th>
              <th style={{ width: 120, textAlign: "right" }}>Subtotal</th>
              <th style={{ width: 180 }}>Notas</th>
              {isEditing && <th style={{ width: 30 }}></th>}
            </tr>
          </thead>
          <tbody>
            {components.map((c, i) => {
              const p = c.piece;
              const unit = Number(p?.price) || 0;
              const qty = Number(c.quantity) || 1;
              return (
                <tr key={i}>
                  <td>
                    {isEditing ? (
                      <PieceLookup
                        value={p ? {
                          pieceId: p._id, sku: p.sku, description: p.name,
                          price: p.price, source: "catalog",
                          temporary: !!p.temporary, expiresAt: p.expiresAt || null,
                        } : null}
                        onChange={v => updateAt(i, {
                          piece: v?.pieceId ? {
                            _id: v.pieceId, sku: v.sku, name: v.description, price: v.price,
                            temporary: !!v.temporary, expiresAt: v.expiresAt || null,
                          } : null,
                        })}
                        placeholder="SKU o nombre del componente..."
                      />
                    ) : p ? (
                      <span>
                        <span className="bini-mono" style={{ color: "#3498db" }}>{p.sku}</span>
                        <span style={{ color: "#ccc", marginLeft: 8 }}>{p.name}</span>
                      </span>
                    ) : <span style={{ color: "#aaa" }}>—</span>}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {isEditing ? (
                      <input type="number" min={0} step={1} className="bini-input"
                        style={{ width: 60, textAlign: "right" }}
                        value={qty}
                        onFocus={e => e.target.select()}
                        onChange={e => updateAt(i, { quantity: Number(e.target.value) || 0 })} />
                    ) : qty}
                  </td>
                  <td style={{ textAlign: "right", color: "#aaa" }}>
                    {unit > 0 ? `$${fmtN(unit)}` : "—"}
                  </td>
                  <td style={{ textAlign: "right", color: "#a78bfa", fontWeight: 600 }}>
                    {unit > 0 ? `$${fmtN(unit * qty)}` : "—"}
                  </td>
                  <td>
                    {isEditing ? (
                      <input className="bini-input" style={{ fontSize: 11 }}
                        value={c.notes || ""}
                        placeholder="ej: con perno hex"
                        onChange={e => updateAt(i, { notes: e.target.value })} />
                    ) : (
                      <span style={{ color: "#cbd5e1", fontSize: 11 }}>{c.notes || "—"}</span>
                    )}
                  </td>
                  {isEditing && (
                    <td>
                      <button onClick={() => removeAt(i)} className="bini-btn-icon danger"
                        title="Quitar componente">✕</button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {isEditing && (
        <button onClick={addEmpty} className="bini-btn"
          style={{ background: "#1f1f1f", border: "1px solid #3a3a3a", color: "#aaa",
            padding: "5px 12px", fontSize: 11, borderRadius: 4, cursor: "pointer" }}>
          + Agregar componente
        </button>
      )}
    </div>
  );
};

// ─── HistorialTab ──────────────────────────────────────────────────────────
const HistorialTab = ({ history, metrics, historyRows }) => {
  if (history === undefined || history === null) {
    return <div style={{ color: "#999", fontSize: 11 }}>Cargando historial...</div>;
  }
  if (metrics.workOrdersCount === 0) {
    return (
      <div style={{ color: "#999", fontSize: 11, padding: "10px 0", borderTop: "1px dashed #2e2e2e" }}>
        Esta pieza nunca se fabricó.
      </div>
    );
  }
  const fmtD = d => d ? new Date(d).toLocaleDateString("es-CL", { day:"2-digit", month:"short", year:"2-digit" }) : "—";
  return (
    <>
      <div className="grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 12 }}>
        <MetricCard label="Veces fabricada" value={metrics.totalTimesFabricated}
          sub={`en ${metrics.workOrdersCount} OT${metrics.workOrdersCount !== 1 ? "s" : ""}`} color="#3498db" />
        <MetricCard label="Precio prom." value={`$${fmtN(metrics.avgPUnit)}`} sub="por unidad" color="#2ecc71" />
        <MetricCard label="Último cliente" value={metrics.lastClient || "—"} sub={fmtD(metrics.lastDate)} color="#a78bfa" small />
        <MetricCard label="Primera vez" value={fmtD(metrics.firstDate)} sub={`hasta ${fmtD(metrics.lastDate)}`} color="#888" small />
      </div>
      <table className="bini-items-table">
        <thead>
          <tr>
            <th style={{ width: 85 }}>Fecha</th>
            <th style={{ width: 80 }}>N° OT</th>
            <th>Cliente</th>
            <th style={{ width: 55, textAlign: "right" }}>Cant</th>
            <th style={{ width: 100, textAlign: "right" }}>P. unit</th>
            <th style={{ width: 110, textAlign: "right" }}>Subtotal</th>
            <th style={{ width: 100 }}>Estado</th>
          </tr>
        </thead>
        <tbody>
          {historyRows.map(r => (
            <tr key={r._id}>
              <td style={{ color: "#cbd5e1", fontSize: 11 }}>{fmtD(r.date)}</td>
              <td><span className="bini-mono" style={{ color: "#3498db", fontWeight: 600 }}>{r.number}</span></td>
              <td style={{ color: "#ccc" }}>{r.client?.name || "—"}</td>
              <td style={{ textAlign: "right" }}>{r.quantity}</td>
              <td style={{ textAlign: "right" }}>${fmtN(r.pUnit)}</td>
              <td style={{ textAlign: "right" }}>${fmtN(r.subtotal)}</td>
              <td style={{ fontSize: 10, color: "#cbd5e1" }}>{STATUS_LABELS[r.status] || r.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

// ─── PriceCard ─────────────────────────────────────────────────────────────
const PriceCard = ({ label, value, color, isEditing, editKey, helpers, onClick, computed }) => {
  const has = value > 0;
  return (
    <div
      onClick={isEditing ? undefined : e => { e.stopPropagation(); onClick && onClick(); }}
      style={{
        background: "#1f1f1f", border: `1px solid ${has ? color + "40" : "#2e2e2e"}`,
        borderRadius: 6, padding: "10px 12px",
        cursor: isEditing ? "default" : "pointer",
        transition: "border-color .15s",
      }}>
      <div style={{ fontSize: 10, color: "#cbd5e1", textTransform: "uppercase",
        letterSpacing: ".05em", marginBottom: 4 }}>
        {label}{computed && <span style={{ marginLeft: 6, color: "#aaa" }}>(calculado)</span>}
      </div>
      {isEditing ? (
        <input type="number" onFocus={e => e.target.select()} min={0} className="bini-input"
          value={value || 0}
          onChange={e => helpers.updateDraft(d => ({ ...d, [editKey]: Number(e.target.value) }))} />
      ) : (
        <div style={{ fontSize: 16, fontWeight: 700, color: has ? color: "#999" }}>
          {has ? `$${fmtN(value)}` : "—"}
        </div>
      )}
    </div>
  );
};

// ─── StatusChip ────────────────────────────────────────────────────────────
const CHIP_BG = {
  "#2ecc71": "#0a1f15",  // verde
  "#3498db": "#0d1e2e",  // azul
  "#f39c12": "#2d2000",  // ámbar
  "#a78bfa": "#1a1230",  // violeta (conjuntos)
};
const StatusChip = ({ on, label, icon, colorOn }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 5,
    padding: "3px 9px", borderRadius: 12, fontSize: 11,
    background: on ? (CHIP_BG[colorOn] || "#2d2000") : "#1a1a1a",
    color: on ? colorOn : "#555",
    border: `1px solid ${on ? colorOn + "40" : "#2e2e2e"}`,
  }}>
    <span>{icon}</span>{label}
  </span>
);

// ─── PriceDetailModal ──────────────────────────────────────────────────────
// Modal de detalle de precio.
//   - P1 / P2: timeline de priceHistory (P1 usa piece.priceHistory; P2 no tiene
//     historial separado todavía, sólo el valor actual).
//   - P3: desglose del cálculo desde piece.processes + piece.constants.prices.
const PriceDetailModal = ({ piece, mode, onClose, onEditProcesses }) => {
  const fmtDate = d => d ? new Date(d).toLocaleString("es-CL", { dateStyle: "short", timeStyle: "short" }) : "—";

  let title = "";
  let body  = null;

  if (mode === "P1") {
    title = "Rocmin — Precio catálogo";
    const history = (piece.priceHistory || []).slice().reverse();
    body = (
      <>
        <div style={{ fontSize: 24, fontWeight: 700, color: "#2ecc71", marginBottom: 4 }}>
          ${fmtN(piece.price)}
        </div>
        <div style={{ fontSize: 11, color: "#cbd5e1", marginBottom: 16 }}>
          Precio actual del catálogo para Rocmin.
        </div>
        <div style={{ fontSize: 11, fontWeight: 600, color: "#cbd5e1", textTransform: "uppercase",
          letterSpacing: ".05em", marginBottom: 8 }}>
          Historial de cambios ({history.length})
        </div>
        {history.length === 0 ? (
          <div style={{ color: "#999", fontSize: 11 }}>Sin historial registrado.</div>
        ) : (
          <table className="bini-items-table">
            <thead>
              <tr>
                <th style={{ width: 140 }}>Fecha</th>
                <th style={{ width: 120, textAlign: "right" }}>Precio</th>
                <th>Cambiado por</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h, i) => (
                <tr key={i}>
                  <td style={{ color: "#cbd5e1", fontSize: 11 }}>{fmtDate(h.changedAt)}</td>
                  <td style={{ textAlign: "right", color: "#2ecc71", fontWeight: 600 }}>${fmtN(h.price)}</td>
                  <td style={{ color: "#ccc", fontSize: 11 }}>{h.changedBy || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </>
    );
  } else if (mode === "P2") {
    title = "General — Precio catálogo";
    body = (
      <>
        <div style={{ fontSize: 24, fontWeight: 700, color: "#3498db", marginBottom: 4 }}>
          {piece.priceClient > 0 ? `$${fmtN(piece.priceClient)}` : "—"}
        </div>
        <div style={{ fontSize: 11, color: "#cbd5e1", marginBottom: 16 }}>
          Precio del catálogo aplicado a clientes que no son Rocmin.
        </div>
        <div style={{ padding: 10, background: "#1a1a1a", border: "1px solid #2e2e2e",
          borderRadius: 5, fontSize: 11, color: "#cbd5e1" }}>
          ℹ Historial separado de P2 aún no implementado.
          Cambios manuales no quedan trazados todavía.
        </div>
      </>
    );
  } else if (mode === "P3") {
    title = "Costo proceso (neto)";
    const editButton = onEditProcesses && (
      <button onClick={onEditProcesses}
        style={{
          background: "#3498db", color: "#fff", border: "none",
          borderRadius: 5, padding: "6px 14px", fontSize: 12,
          fontWeight: 600, cursor: "pointer",
        }}>
        ✎ Editar procesos y tarifas
      </button>
    );
    const procs = piece.processes || [];
    const prices = piece.constants?.prices;
    const rows = [];
    let total = 0;
    for (const proc of procs) {
      if (proc.external) {
        const sub = Number(proc.price) || 0;
        rows.push({ name: proc.name || "Proceso externo", kind: "externo",
          duration: `${proc.duration || 0} d`, rate: "—", subtotal: sub });
        total += sub;
      } else if (proc.machineType && prices) {
        const mtId = proc.machineType._id || proc.machineType;
        const row = prices.find(r => String(r.machineType) === String(mtId));
        const sel = proc.priceSelected || "price";
        const rate = row?.[sel] || 0;
        const sub = (rate * (proc.duration || 0)) / 60;
        rows.push({ name: proc.name || proc.machineType.type || "Proceso",
          kind: proc.machineType.type || "interno",
          duration: `${proc.duration || 0} min`, rate: `$${fmtN(rate)}/h (${sel})`,
          subtotal: sub });
        total += sub;
      } else {
        rows.push({ name: proc.name || "Proceso", kind: "incompleto",
          duration: `${proc.duration || 0}`, rate: "sin tarifa/máquina", subtotal: 0 });
      }
    }
    body = (
      <>
        <div style={{ fontSize: 24, fontWeight: 700, color: "#a78bfa", marginBottom: 4 }}>
          {total > 0 ? `$${fmtN(Math.round(total))}` : "—"}
        </div>
        <div style={{ fontSize: 11, color: "#cbd5e1", marginBottom: 12 }}>
          Neto desde tiempos × tarifa máquina (internos) + precio fijo (externos).
          <br />Sin costo de material, sin porcentaje de utilidad.
        </div>
        {editButton && (
          <div style={{ marginBottom: 14 }}>
            {editButton}
            <span style={{ marginLeft: 10, fontSize: 11, color: "#aaa" }}>
              Modificá duración, máquina o tarifa (General / Específica / Mínima) por proceso.
            </span>
          </div>
        )}
        {rows.length === 0 ? (
          <div style={{ padding: 12, background: "#1a1a1a", border: "1px solid #2e2e2e",
            borderRadius: 5, fontSize: 11, color: "#cbd5e1" }}>
            Esta pieza no tiene procesos cargados. Cargá procesos en el form de creación / edición
            para que P3 se calcule.
          </div>
        ) : (
          <>
            <table className="bini-items-table">
              <thead>
                <tr>
                  <th>Proceso</th>
                  <th style={{ width: 100 }}>Tipo</th>
                  <th style={{ width: 90, textAlign: "right" }}>Duración</th>
                  <th style={{ width: 160, textAlign: "right" }}>Tarifa</th>
                  <th style={{ width: 110, textAlign: "right" }}>Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td style={{ color: "#ccc" }}>{r.name}</td>
                    <td style={{ color: "#cbd5e1", fontSize: 11 }}>{r.kind}</td>
                    <td style={{ textAlign: "right", color: "#aaa" }}>{r.duration}</td>
                    <td style={{ textAlign: "right", color: "#aaa", fontSize: 11 }}>{r.rate}</td>
                    <td style={{ textAlign: "right", color: r.subtotal > 0 ? "#a78bfa" : "#555", fontWeight: 600 }}>
                      {r.subtotal > 0 ? `$${fmtN(Math.round(r.subtotal))}` : "—"}
                    </td>
                  </tr>
                ))}
                <tr style={{ borderTop: "2px solid #3a3a3a" }}>
                  <td colSpan={4} style={{ textAlign: "right", color: "#cbd5e1",
                    fontWeight: 600, fontSize: 11, paddingTop: 8 }}>TOTAL NETO</td>
                  <td style={{ textAlign: "right", color: "#a78bfa", fontWeight: 700,
                    fontSize: 14, paddingTop: 8 }}>${fmtN(Math.round(total))}</td>
                </tr>
              </tbody>
            </table>
          </>
        )}
      </>
    );
  }

  return (
    <div onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,.6)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1000, padding: 20,
      }}>
      <div onClick={e => e.stopPropagation()}
        style={{
          background: "#1a1a1a", border: "1px solid #2e2e2e", borderRadius: 8,
          padding: 22, maxWidth: 720, width: "100%", maxHeight: "85vh",
          overflow: "auto", color: "#ddd",
        }}>
        <div style={{ display: "flex", justifyContent: "space-between",
          alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>{title}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span className="bini-mono" style={{ color: "#3498db", fontSize: 11 }}>{piece.sku}</span>
            <button onClick={onClose} className="bini-btn-icon">✕</button>
          </div>
        </div>
        <div style={{ fontSize: 11, color: "#cbd5e1", marginBottom: 14 }}>{piece.name}</div>
        {body}
      </div>
    </div>
  );
};

export default Pieces;
